import type { SessionService } from '@efdigital/ll-addon-auth';
import {
  isCancelled,
  isHistory,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-lifecycle';
import type {
  CoachingSession,
  CoachingSessionNote,
} from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import { asAsyncBelongsTo } from '@efdigital/ll-xceed-v2/data/async-setter';
import {
  createSessionNote,
  querySessionNotesBySession,
  updateSessionNote,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/session-notes';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';

interface SessionNotesSignature {
  Args: {
    session: CoachingSession;
    /** Decides the wording shown to the author; the write itself is role-agnostic. */
    role: 'coach' | 'coachee';
  };
  Element: HTMLDivElement;
}

/**
 * The author's saved note, if one exists.
 *
 * The query sorts newest first, so should a race ever produce a duplicate, the
 * current note is the one that gets edited.
 */
function existingNote(
  notes: readonly CoachingSessionNote[]
): CoachingSessionNote | undefined {
  return notes[0];
}

/**
 * Splits a note body into paragraphs for the read-only view.
 *
 * Bodies are plain text and may contain newlines; Bootstrap has no white-space
 * utility, so paragraphs are rendered rather than adding app CSS for `pre-wrap`.
 */
function paragraphs(body: string): string[] {
  return body.split('\n').filter((line) => line.trim().length > 0);
}

/**
 * Length beyond which a note is collapsed in the read-only view.
 *
 * Roughly a short paragraph: long enough that most notes are unaffected, short
 * enough that a full session write-up does not push the rest of the card off screen.
 */
const COLLAPSE_LENGTH = 400;

/**
 * Trims a body to {@link COLLAPSE_LENGTH}, breaking on a word boundary.
 *
 * The collapsed view renders genuinely shortened text rather than hiding overflow
 * with CSS, so what is read aloud or copied matches what is shown.
 */
function collapseBody(body: string): string {
  if (body.length <= COLLAPSE_LENGTH) {
    return body;
  }

  const slice = body.slice(0, COLLAPSE_LENGTH);
  const lastSpace = slice.lastIndexOf(' ');
  // Only honour the word boundary if it is not so early that we lose most of the
  // preview -- a single very long word should still fill the space.
  const cut =
    lastSpace > COLLAPSE_LENGTH * 0.6 ? slice.slice(0, lastSpace) : slice;

  return `${cut.trimEnd()}...`;
}

function formatSavedAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The author's private note for one coaching session.
 *
 * One note per person per session, edited in place. Notes are private to whoever
 * wrote them: a coach's note is never returned to the coachee and vice versa,
 * enforced by ll-api's standard `user`-ownership handlers rather than by anything
 * here. There is deliberately no share control - surfacing something to the other
 * party belongs to coach/coachee messaging (EFDL-1155 / EFDL-1156).
 */
export default class SessionNotes extends Component<SessionNotesSignature> {
  @service declare store: Store;
  @service declare session: SessionService;

  /** Unsaved edits. `null` means "not yet edited", so the loaded note shows through. */
  @tracked draft: string | null = null;
  @tracked isEditing = false;
  /** Whether a long note is shown in full. Read-only view only. */
  @tracked expanded = false;
  @tracked saving = false;
  @tracked errorMessage: string | null = null;
  /** Set after a successful save so the timestamp updates without a refetch. */
  @tracked savedAt: string | null = null;

  @tracked notesQuery = querySessionNotesBySession(this.args.session.id!);

  get privacyHint(): string {
    return this.args.role === 'coach'
      ? 'Your coachee cannot see this'
      : 'Your coach cannot see this';
  }

  /**
   * Whether the session is still ahead of the author, which is what decides between
   * a prep prompt and a write-up prompt.
   *
   * Cancelled sessions are deliberately excluded: `isHistory` is false for them, but
   * they render on the history tab and are never going to happen, so prompting for
   * what you want to cover would be wrong.
   */
  get isUpcoming(): boolean {
    const { status } = this.args.session;
    return !isHistory(status) && !isCancelled(status);
  }

  get placeholder(): string {
    if (this.args.role === 'coach') {
      return this.isUpcoming
        ? 'What you want to cover, anything to follow up from last time...'
        : 'What was discussed, what you observed, what to pick up next time...';
    }

    return this.isUpcoming
      ? 'Questions or topics you want to raise in this session...'
      : 'Reflections, takeaways, actions you agreed...';
  }

  // These are arrow-function fields rather than methods because Glimmer invokes
  // them from the template as plain functions, which would leave `this` undefined.

  /** What the textarea shows: unsaved edits if any, otherwise the saved body. */
  bodyFor = (note: CoachingSessionNote | undefined): string => {
    return this.draft ?? note?.body ?? '';
  };

  savedLabel = (note: CoachingSessionNote | undefined): string | null => {
    const timestamp = this.savedAt ?? note?.modified;
    return timestamp ? `Saved ${formatSavedAt(timestamp)}` : null;
  };

  isDirty = (note: CoachingSessionNote | undefined): boolean => {
    return this.draft !== null && this.draft !== (note?.body ?? '');
  };

  saveDisabled = (note: CoachingSessionNote | undefined): boolean => {
    return this.saving || !this.isDirty(note);
  };

  editLabel = (note: CoachingSessionNote | undefined): string => {
    return note?.body ? 'Edit note' : 'Add note';
  };

  /** Whether this note is long enough to be worth collapsing. */
  isLong = (note: CoachingSessionNote | undefined): boolean => {
    return (note?.body?.length ?? 0) > COLLAPSE_LENGTH;
  };

  /** The paragraphs to render, collapsed unless the reader expanded them. */
  shownParagraphs = (note: CoachingSessionNote | undefined): string[] => {
    const body = note?.body ?? '';
    return paragraphs(this.expanded ? body : collapseBody(body));
  };

  toggleExpanded = () => {
    this.expanded = !this.expanded;
  };

  startEditing = (note: CoachingSessionNote | undefined) => {
    this.draft = note?.body ?? '';
    this.errorMessage = null;
    this.isEditing = true;
    // The editor always shows the whole note, so returning to the read-only view
    // should start collapsed again rather than restoring a stale expanded state.
    this.expanded = false;
  };

  cancelEditing = () => {
    this.draft = null;
    this.errorMessage = null;
    this.isEditing = false;
  };

  setDraft = (event: Event) => {
    this.draft = (event.target as HTMLTextAreaElement).value;
  };

  save = async (note: CoachingSessionNote | undefined) => {
    const body = (this.draft ?? '').trim();
    if (this.saving || !this.isDirty(note)) {
      return;
    }

    this.saving = true;
    this.errorMessage = null;

    try {
      if (note) {
        const previousBody = note.body;
        note.body = body;
        try {
          await this.store.request(updateSessionNote(note, this.store));
        } catch (error) {
          // The builder patches from the live cached record, so the mutation above
          // is unavoidable before the request -- revert it on failure so a cancel
          // doesn't surface an edit that was never persisted.
          note.body = previousBody;
          throw error;
        }
        this.savedAt = note.modified;
        this.draft = null;
      } else {
        // `user` must be the caller's own id: ll-api rejects a create whose user
        // does not match the JWT sub, so a note cannot be attributed to someone else.
        const created = this.store.createRecord<CoachingSessionNote>(
          DataTypeCoaching.CoachingSessionNote,
          {
            coachingSession: asAsyncBelongsTo(this.args.session),
            user: this.session.cached.userId!,
            body,
          }
        );

        try {
          await this.store.request(createSessionNote(created, this.store));
          this.draft = null;
          // Refetch so the note is loaded through the same path as on first render,
          // which keeps subsequent edits pointing at the persisted record.
          this.notesQuery = querySessionNotesBySession(this.args.session.id!);
        } catch (error) {
          // Drop the unsaved record, otherwise it lingers and a retry would submit
          // a second note rather than retrying the first.
          created.unloadRecord();
          throw error;
        }
      }
      this.isEditing = false;
    } catch (error) {
      console.error(error);
      this.errorMessage = 'Your note could not be saved. Please try again.';
      // Stay open on failure so the text is not lost.
    } finally {
      this.saving = false;
    }
  };

  <template>
    {{! Outer spacing is left to the caller so the two session cards control how
        this sits against the rest of the card. }}
    <div class="border-top pt-3" ...attributes>
      <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
        <iconify-icon
          inline
          icon="ph:lock-simple"
          class="text-body-secondary"
          aria-hidden="true"
        />
        <span class="gud-eyebrow mb-0">Your private note</span>
        <span class="text-body-secondary" aria-hidden="true">&middot;</span>
        <small
          id="session-note-privacy-{{@session.id}}"
          class="text-body-secondary"
        >{{this.privacyHint}}</small>
      </div>

      <Request @query={{this.notesQuery}}>
        <:content as |result|>
          {{#let (existingNote result.data) as |note|}}
            {{#if this.isEditing}}
              <div aria-busy={{this.saving}}>
                <label
                  for="session-note-{{@session.id}}"
                  class="form-label visually-hidden"
                >Your private note about this session</label>
                <textarea
                  id="session-note-{{@session.id}}"
                  class="form-control"
                  rows="4"
                  placeholder={{this.placeholder}}
                  value={{this.bodyFor note}}
                  disabled={{this.saving}}
                  aria-describedby="session-note-privacy-{{@session.id}}"
                  {{on "input" this.setDraft}}
                ></textarea>

                <div
                  class="d-flex flex-wrap justify-content-between align-items-center gap-2 border-top mt-3 pt-3"
                >
                  {{! Privacy wording lives in the header above, which the textarea
                      references via aria-describedby. }}
                  <small class="text-body-secondary">
                    {{#if (this.savedLabel note)}}
                      {{this.savedLabel note}}
                    {{/if}}
                  </small>
                  <div class="d-flex gap-2">
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      disabled={{this.saving}}
                      {{on "click" this.cancelEditing}}
                    >Cancel</button>
                    <button
                      type="button"
                      class="btn btn-primary btn-sm"
                      disabled={{this.saveDisabled note}}
                      aria-busy={{this.saving}}
                      {{on "click" (fn this.save note)}}
                    >{{if this.saving "Saving..." "Save"}}</button>
                  </div>
                </div>
              </div>
            {{else}}
              <div>
                {{#if note.body}}
                  <div
                    id="session-note-body-{{@session.id}}"
                    class="border-start border-3 border-primary ps-3 py-1 d-flex flex-column gap-3"
                  >
                    {{#each (this.shownParagraphs note) as |paragraph|}}
                      <p class="mb-0 fs-6 lh-lg text-body">{{paragraph}}</p>
                    {{/each}}
                  </div>
                  {{#if (this.isLong note)}}
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0 mt-2 text-decoration-none"
                      aria-expanded={{if this.expanded "true" "false"}}
                      aria-controls="session-note-body-{{@session.id}}"
                      {{on "click" this.toggleExpanded}}
                    >{{if this.expanded "Show less" "Show more"}}</button>
                  {{/if}}
                {{else}}
                  <p
                    class="mb-0 text-body-secondary fst-italic"
                  >{{this.placeholder}}</p>
                {{/if}}

                <div
                  class="d-flex flex-wrap justify-content-between align-items-center gap-2 border-top mt-3 pt-3"
                >
                  <small class="text-body-secondary">
                    {{#if (this.savedLabel note)}}
                      {{this.savedLabel note}}
                    {{/if}}
                  </small>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm flex-shrink-0"
                    {{on "click" (fn this.startEditing note)}}
                  >{{this.editLabel note}}</button>
                </div>
              </div>
            {{/if}}
          {{/let}}
        </:content>
        <:loading>
          <p
            class="small text-body-secondary mb-0"
            role="status"
            aria-live="polite"
          >
            Loading your private note…
          </p>
        </:loading>
        <:error>
          <div class="alert alert-warning mb-0 py-2 px-3" role="alert">
            <small>Your note could not be loaded.</small>
          </div>
        </:error>
      </Request>

      {{#if this.errorMessage}}
        <div class="alert alert-danger mb-0 mt-3 py-2 px-3" role="alert">
          <small>{{this.errorMessage}}</small>
        </div>
      {{/if}}
    </div>
  </template>
}
